/**
 * Flexible Drawer Analytics - Client Side
 * Sends data to hosted analytics app
 */

class FlexibleDrawerAnalytics {
  constructor() {
    this.apiEndpoint = window.FD_ANALYTICS_ENDPOINT || 'http://localhost:3001/api/analytics';
    this.sessionId = this.getOrCreateSessionId();
    this.maxRetries = 3;
    
    this.init();
  }

  init() {
    console.log('Flexible Drawer Analytics initialized');
    this.setupEventListeners();
  }

  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('fd_session_id');
    if (!sessionId) {
      sessionId = 'fd_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('fd_session_id', sessionId);
    }
    return sessionId;
  }

  setupEventListeners() {
    // Listen for flexible drawer clicks
    document.addEventListener('click', (event) => {
      this.handleClick(event);
    });

    // Listen for drawer opens/closes
    document.addEventListener('DOMContentLoaded', () => {
      this.setupDrawerObservers();
    });
  }

  handleClick(event) {
    const target = event.target;
    
    if (this.isFlexibleDrawerElement(target)) {
      this.trackEvent('drawer_click', {
        element: this.getElementInfo(target),
        product: this.getProductInfo(),
        page: this.getPageInfo(),
        timestamp: Date.now()
      });
    }
  }

  isFlexibleDrawerElement(element) {
    return element.closest('#flexible-drawer-') ||
           element.closest('.fd-trigger-btn') ||
           element.closest('.fd-modal-opener') ||
           element.closest('.option-card') ||
           element.closest('.fd-add-cart-button') ||
           element.closest('[data-block-id]') ||
           element.closest('.car-kit-builder') ||
           element.closest('#CarKit-Drawer');
  }

  getElementInfo(element) {
    const drawerElement = element.closest('#flexible-drawer-');
    const blockId = drawerElement ? drawerElement.id.replace('flexible-drawer-', '') : null;
    
    return {
      tagName: element.tagName,
      className: element.className,
      id: element.id,
      blockId: blockId,
      text: element.textContent?.trim().substring(0, 100) || '',
      dataAttributes: this.getDataAttributes(element)
    };
  }

  getDataAttributes(element) {
    const attrs = {};
    for (let attr of element.attributes) {
      if (attr.name.startsWith('data-')) {
        attrs[attr.name] = attr.value;
      }
    }
    return attrs;
  }

  getProductInfo() {
    const productData = {
      id: null,
      title: null,
      handle: null,
      price: null,
      vendor: null
    };

    // Check for Shopify product object
    if (window.Shopify && window.Shopify.product) {
      Object.assign(productData, {
        id: window.Shopify.product.id,
        title: window.Shopify.product.title,
        handle: window.Shopify.product.handle,
        price: window.Shopify.product.price,
        vendor: window.Shopify.product.vendor
      });
    }

    // Check for product data in script tags
    const productScript = document.querySelector('script[type="application/json"][id*="product"]');
    if (productScript) {
      try {
        const data = JSON.parse(productScript.textContent);
        if (data.product) {
          Object.assign(productData, {
            id: data.product.id,
            title: data.product.title,
            handle: data.product.handle,
            price: data.product.price,
            vendor: data.product.vendor
          });
        }
      } catch (e) {
        console.warn('Failed to parse product data:', e);
      }
    }

    return productData;
  }

  getPageInfo() {
    return {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timestamp: Date.now()
    };
  }

  setupDrawerObservers() {
    // Observe drawer state changes
    const drawers = document.querySelectorAll('#flexible-drawer-');
    
    drawers.forEach(drawer => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
            const isExpanded = drawer.getAttribute('aria-expanded') === 'true';
            this.trackEvent(isExpanded ? 'drawer_opened' : 'drawer_closed', {
              blockId: drawer.id.replace('flexible-drawer-', ''),
              product: this.getProductInfo(),
              page: this.getPageInfo(),
              timestamp: Date.now()
            });
          }
        });
      });

      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    });

    // Observe modal state changes
    const modals = document.querySelectorAll('#CarKit-Drawer');
    
    modals.forEach(modal => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            const isOpen = modal.hasAttribute('open');
            this.trackEvent(isOpen ? 'modal_opened' : 'modal_closed', {
              modalId: modal.id,
              product: this.getProductInfo(),
              page: this.getPageInfo(),
              timestamp: Date.now()
            });
          }
        });
      });

      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open']
      });
    });
  }

  trackEvent(eventType, eventData) {
    const event = {
      eventId: this.generateEventId(),
      sessionId: this.sessionId,
      eventType: eventType,
      data: eventData,
      timestamp: Date.now()
    };

    this.sendToServer(event);
    console.log('Flexible Drawer Event:', event);
  }

  generateEventId() {
    return 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  async sendToServer(event) {
    try {
      const response = await fetch(`${this.apiEndpoint}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Event sent successfully:', result);

    } catch (error) {
      console.warn('Failed to send event to server:', error);
      // Could implement retry logic here
    }
  }
}

// Initialize analytics when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.FlexibleDrawerAnalytics = new FlexibleDrawerAnalytics();
});

// Make analytics available globally
window.FD_Analytics = {
  getData: () => ({ message: 'Data is stored on the server. Visit the dashboard to view analytics.' }),
  getProductData: (productId) => ({ message: 'Data is stored on the server. Visit the dashboard to view analytics.' }),
  getSessionData: () => ({ message: 'Data is stored on the server. Visit the dashboard to view analytics.' }),
  exportData: (format) => ({ message: 'Data is stored on the server. Visit the dashboard to export data.' }),
  clearData: () => ({ message: 'Data is stored on the server. Visit the dashboard to clear data.' })
};
